import { type Dial, type InstrumentFilters } from "./dials";
import { newCustomInstrument, sameInstrument } from "./eq";
import type { VocalProfile } from "./vocals";

export type HealingTone = {
  hz: number;
  name: string;
  blurb: string;
};

export const HEALING_TONES: HealingTone[] = [
  { hz: 174, name: "Foundation", blurb: "Low grounding drone. Slow, heavy, sleep-adjacent." },
  { hz: 285, name: "Regen", blurb: "Warm body-tone bed under a soft pulse." },
  { hz: 396, name: "Release", blurb: "Earth drone for letting-go / unburden pieces." },
  { hz: 417, name: "Change", blurb: "Clearing tone. Gentle motion, not a drop." },
  { hz: 432, name: "Earth tune", blurb: "Concert A at 432 Hz instead of 440. Whole mix sits there." },
  { hz: 528, name: "Heart", blurb: "The love / repair tone. Singing bowls and open vowels." },
  { hz: 639, name: "Bond", blurb: "Connection piece. Soft harmony, two-voice air." },
  { hz: 741, name: "Expression", blurb: "Brighter cleanse. Flute, air, light mantra." },
  { hz: 852, name: "Insight", blurb: "High crystal partials and long decays." },
  { hz: 963, name: "Crown", blurb: "Very sparse overtone bed. Almost no drums." },
];

export type HealingPreset = {
  id: string;
  label: string;
  tones: number[];
  bpm: number;
  duration: number;
  genres: string[];
  bowl: string;
  lyrics: string;
  instruments: string[];
  drums: number;
  masterEq: InstrumentFilters;
  style: Record<string, number>;
  vocal: Partial<VocalProfile>;
};

const INST = `[instrumental]`;
const MANTRA_528 = `[intro]
${INST}
[verse]
Mmm ah, mm-mm
Hold this light
[chorus]
Ah — ah, we are here
[outro]
${INST}`;

export const HEALING_PRESETS: HealingPreset[] = [
  {
    id: "sleep-174",
    label: "Deep sleep 174",
    tones: [174, 396],
    bpm: 52,
    duration: 180,
    genres: ["healing", "ambient", "drone"],
    bowl: "A deep-sleep sound-healing bed in 174 Hz. No beat drop. Distant singing bowls, sub drone, almost no vocal. Dark room, slow breath.",
    lyrics: `[intro]\n${INST}\n[verse]\n${INST}\n[outro]\n${INST}`,
    instruments: ["Singing bowls", "Gong", "Tanpura"],
    drums: 4,
    masterEq: { hpf: 0, sub: 72, bass: 64, lowmid: 42, presence: 28, air: 36, lpf: 28 },
    style: { energy: 18, groove: 8, vintage: 36, space: 82, dark: 62, punch: 14, polish: 48 },
    vocal: { delivery: "instrumental", mic: "room" },
  },
  {
    id: "release-396",
    label: "Release 396",
    tones: [396],
    bpm: 60,
    duration: 150,
    genres: ["healing", "meditation"],
    bowl: "A 396 Hz release meditation. Didgeridoo and singing bowls, earthy, unhurried. Soft voice optional, no pop hook.",
    lyrics: `[intro]\n${INST}\n[verse]\nLet it go\nLet it fall\n[chorus]\nAh — I am light\n[outro]\n${INST}`,
    instruments: ["Didgeridoo", "Singing bowls", "Frame drum"],
    drums: 10,
    masterEq: { hpf: 4, sub: 66, bass: 60, lowmid: 58, presence: 34, air: 40, lpf: 18 },
    style: { energy: 22, groove: 16, vintage: 40, space: 74, dark: 50, punch: 18, polish: 46 },
    vocal: { delivery: "sung", mic: "room" },
  },
  {
    id: "change-417",
    label: "Change 417",
    tones: [417],
    bpm: 68,
    duration: 150,
    genres: ["healing", "ambient"],
    bowl: "A 417 Hz clearing piece. Flute and harp over a warm drone. Morning light, gentle forward motion, no drums in the face.",
    lyrics: `[intro]\n${INST}\n[verse]\nOpen the door\n[chorus]\nWe begin again\n[outro]\n${INST}`,
    instruments: ["Flute", "Harp", "Singing bowls"],
    drums: 8,
    masterEq: { hpf: 8, sub: 48, bass: 52, lowmid: 62, presence: 44, air: 56, lpf: 10 },
    style: { energy: 28, groove: 14, vintage: 30, space: 70, dark: 32, punch: 20, polish: 52 },
    vocal: { delivery: "sung", mic: "room" },
  },
  {
    id: "earth-432",
    label: "Earth 432",
    tones: [432],
    bpm: 72,
    duration: 160,
    genres: ["healing", "folk", "ambient"],
    bowl: "An acoustic sound-healing song tuned to 432 Hz concert pitch, not 440. Soft guitar, cello, close gentle vocal. Warm, human, unhurried.",
    lyrics: `[intro]\n${INST}\n[verse]\nThe ground remembers my name\nSlow river, same sky\n[chorus]\nWe tune to the earth\nWe breathe in time\n[outro]\n${INST}`,
    instruments: ["Guitar", "Cello", "Harp"],
    drums: 16,
    masterEq: { hpf: 6, sub: 44, bass: 54, lowmid: 56, presence: 48, air: 52, lpf: 8 },
    style: { energy: 32, groove: 22, vintage: 44, space: 62, dark: 30, punch: 24, polish: 54 },
    vocal: { delivery: "sung", mic: "close" },
  },
  {
    id: "heart-528",
    label: "Heart 528",
    tones: [528],
    bpm: 64,
    duration: 180,
    genres: ["healing", "meditation"],
    bowl: "A 528 Hz heart-frequency sound-healing piece. Crystal and singing bowls, harp, long vowels. Love, repair, open chest. Not a pop ballad.",
    lyrics: MANTRA_528,
    instruments: ["Singing bowls", "Crystal bowls", "Harp"],
    drums: 6,
    masterEq: { hpf: 6, sub: 50, bass: 48, lowmid: 64, presence: 42, air: 58, lpf: 8 },
    style: { energy: 24, groove: 10, vintage: 34, space: 80, dark: 28, punch: 16, polish: 56 },
    vocal: { delivery: "sung", mic: "room" },
  },
  {
    id: "bond-639",
    label: "Bond 639",
    tones: [639],
    bpm: 70,
    duration: 150,
    genres: ["healing", "soul"],
    bowl: "A 639 Hz connection piece. Two-voice air, piano, soft strings. Warm room, holding, no snare crack.",
    lyrics: `[intro]\n${INST}\n[verse]\nStay with me in the quiet\n[chorus]\nWe are not alone\nAh — we are not alone\n[outro]\n${INST}`,
    instruments: ["Piano / keys", "Strings", "Singing bowls"],
    drums: 12,
    masterEq: { hpf: 8, sub: 42, bass: 50, lowmid: 54, presence: 52, air: 60, lpf: 6 },
    style: { energy: 30, groove: 18, vintage: 32, space: 68, dark: 26, punch: 22, polish: 58 },
    vocal: { delivery: "sung", mic: "close" },
  },
  {
    id: "voice-741",
    label: "Expression 741",
    tones: [741],
    bpm: 76,
    duration: 140,
    genres: ["healing", "ambient"],
    bowl: "A 741 Hz expression / cleanse piece. Flute lead, wind chimes, airy vocal mantra. Bright but not sharp.",
    lyrics: `[intro]\n${INST}\n[verse]\nI speak and the air clears\n[chorus]\nAh — I am heard\n[bridge]\n${INST}\n[outro]\n${INST}`,
    instruments: ["Flute", "Wind chimes", "Crystal bowls"],
    drums: 10,
    masterEq: { hpf: 12, sub: 32, bass: 40, lowmid: 48, presence: 62, air: 68, lpf: 4 },
    style: { energy: 34, groove: 16, vintage: 22, space: 72, dark: 18, punch: 26, polish: 60 },
    vocal: { delivery: "sung", mic: "room" },
  },
  {
    id: "insight-852",
    label: "Insight 852",
    tones: [852],
    bpm: 58,
    duration: 180,
    genres: ["healing", "drone"],
    bowl: "An 852 Hz insight meditation. Crystal bowls, koshi chimes, long overtones. Almost no lyric. Night sky, still body.",
    lyrics: `[intro]\n${INST}\n[verse]\n${INST}\n[outro]\n${INST}`,
    instruments: ["Crystal bowls", "Koshi chimes", "Singing bowls"],
    drums: 2,
    masterEq: { hpf: 14, sub: 28, bass: 34, lowmid: 44, presence: 58, air: 76, lpf: 2 },
    style: { energy: 16, groove: 6, vintage: 24, space: 86, dark: 40, punch: 10, polish: 50 },
    vocal: { delivery: "instrumental", mic: "room" },
  },
  {
    id: "crown-963",
    label: "Crown 963",
    tones: [963],
    bpm: 50,
    duration: 200,
    genres: ["healing", "drone"],
    bowl: "A 963 Hz crown / unity overtone bed. Extremely sparse. Crystal partials, no groove, no hook. Stillness is the arrangement.",
    lyrics: `[intro]\n${INST}\n[outro]\n${INST}`,
    instruments: ["Crystal bowls", "Tuning forks", "Wind chimes"],
    drums: 0,
    masterEq: { hpf: 18, sub: 22, bass: 28, lowmid: 38, presence: 54, air: 80, lpf: 0 },
    style: { energy: 10, groove: 2, vintage: 20, space: 90, dark: 36, punch: 8, polish: 48 },
    vocal: { delivery: "instrumental", mic: "room" },
  },
  {
    id: "solfeggio-stack",
    label: "Solfeggio stack",
    tones: [396, 528, 639],
    bpm: 62,
    duration: 180,
    genres: ["healing", "meditation", "ambient"],
    bowl: "A stacked solfeggio sound-healing journey: 396 Hz earth, 528 Hz heart, 639 Hz bond. Bowls, tanpura, harp. Slow blooming arrangement, not a club mix.",
    lyrics: `[intro]\n${INST}\n[verse]\nMmm — from the ground\n[chorus]\nAh, the heart, the hold\n[bridge]\n${INST}\n[outro]\n${INST}`,
    instruments: ["Singing bowls", "Tanpura", "Harp", "Crystal bowls"],
    drums: 8,
    masterEq: { hpf: 4, sub: 58, bass: 54, lowmid: 60, presence: 40, air: 58, lpf: 12 },
    style: { energy: 26, groove: 12, vintage: 38, space: 78, dark: 38, punch: 16, polish: 54 },
    vocal: { delivery: "sung", mic: "room" },
  },
];

export function toneByHz(hz: number): HealingTone | undefined {
  return HEALING_TONES.find((t) => t.hz === hz);
}

export function isHealingStyle(genres: string[], tones: number[]): boolean {
  if (tones.length) return true;
  return genres.some((g) => /heal|meditat|solfeg|drone|432|528/i.test(g));
}

export function healingCaption(tones: number[], genres: string[], weight = 55): string {
  if (weight <= 0 || !isHealingStyle(genres, tones)) return "";
  const named = tones
    .slice()
    .sort((a, b) => a - b)
    .map((hz) => {
      const t = toneByHz(hz);
      return t ? `${hz} Hz (${t.name})` : `${hz} Hz`;
    });
  const pitch = tones.includes(432)
    ? "Concert pitch A4 = 432 Hz is a Healing-lane request; honor it at this lane's weight, not by erasing other lanes."
    : "If a tonic is needed, sit a Healing-lane drone on the named Hz.";
  const drone =
    named.length > 0
      ? `Healing frequencies (drone / bowl partial / tonic at this lane's weight): ${named.join(", ")}.`
      : "Sound-healing color: drones, bowls, long decay.";
  const take =
    weight > 75
      ? "Healing may lead: soft attacks, long decays, sparse drums."
      : "Keep this as a bed under Frequency / Mimic / Instruments. Do not turn the whole mix into only bowls.";
  return `HEALING FREQUENCY lane at ${Math.round(weight)}/100. ${drone} ${pitch} ${take}`;
}

export function healingLyricHint(tones: number[], genres: string[], weight = 55): string {
  if (weight <= 0 || !isHealingStyle(genres, tones)) return "";
  const hz = tones.length ? ` Atmosphere around ${tones.join(" / ")} Hz is allowed.` : "";
  if (weight < 40) {
    return `HEALING LYRIC NOTES (light, ${Math.round(weight)}/100): keep the song's own words. ${hz}`;
  }
  return (
    `HEALING LYRIC NOTES (${Math.round(weight)}/100): meditation color is welcome.` +
    `${hz} Prefer some [instrumental] space and long vowels, but do not erase a written lyric.`
  );
}

export function applyHealingToDials(dials: Dial[], preset: HealingPreset, uid: (p: string) => string): Dial[] {
  let next = dials;
  for (const name of preset.instruments) {
    const existing = next.find((d) => d.bank === "instruments" && sameInstrument(d.label, name));
    if (existing) continue;
    next = [...next, newCustomInstrument(name, uid)];
  }
  return next;
}
